import { useState, useMemo } from "react";
import axios from "axios";
import { AlertTriangle, Send, Pill, Droplet, Wind, Stethoscope, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useOfflineData } from "@/contexts/OfflineDataContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWeight } from "@/contexts/WeightContext";
import { API } from "@/config/constants";

const SERVICE_OPTIONS = [
  { value: "ground", label: "PCP/ACP-G (Ground)" },
  { value: "sfa_emr", label: "SFA/EMR" },
  { value: "wes", label: "PCP-PS" },
  { value: "air_adult", label: "Air Ambulance — Adult" },
  { value: "air_pediatric", label: "Air Ambulance — OB/NEO/PEDS" },
];

// Ventilator subjects — mirror the tool-mount list in ProtocolDetail
const VENTILATOR_SUBJECTS = [
  { id: "vent-tool-general", name: "Hamilton T1 Ventilator Tool (general)" },
  { id: "air-adult-airway-management", name: "Air Adult — Airway Management" },
  { id: "air-adult-advanced-airway", name: "Air Adult — Advanced Airway" },
  { id: "air-peds-advanced-airway", name: "Air Peds — Advanced Airway" },
  { id: "air-peds-mechanical-ventilation-ref", name: "Air Peds — Mechanical Ventilation Ref" },
];

const CATEGORIES = [
  { value: "protocol",   label: "Protocol",   icon: BookOpen,    desc: "Clinical guideline error or discrepancy" },
  { value: "medication", label: "Medication", icon: Pill,        desc: "Drug dose, route, concentration, or monograph issue" },
  { value: "infusion",   label: "Infusion",   icon: Droplet,     desc: "Infusion rate, mix recipe, or dosing range issue" },
  { value: "ventilator", label: "Ventilator", icon: Wind,        desc: "Hamilton T1 tool or ventilator settings issue" },
  { value: "procedure",  label: "Procedure",  icon: Stethoscope, desc: "Procedure steps or equipment reference" },
];

export default function ReportErrorDialog() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("protocol");
  const [service, setService] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const { protocols, drugs, procedures } = useOfflineData();
  const { infusionDosing } = useWeight();
  const { user } = useAuth();

  // Which list powers the subject <select> depends on category
  const subjectOptions = useMemo(() => {
    if (category === "protocol") {
      if (!service) return [];
      return protocols
        .filter((p) => (p.service || "ground") === service)
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (category === "medication") {
      return (drugs || [])
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (category === "infusion") {
      const infusionDrugs = Object.keys(infusionDosing || {});
      return infusionDrugs
        .map((name) => ({ id: `infusion-${name}`, name: name.charAt(0).toUpperCase() + name.slice(1) + " infusion" }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (category === "ventilator") return VENTILATOR_SUBJECTS;
    if (category === "procedure") {
      return (procedures || [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return [];
  }, [category, service, protocols, drugs, procedures, infusionDosing]);

  const resetFields = () => {
    setCategory("protocol");
    setService("");
    setSubjectId("");
    setDetails("");
  };

  const needsService = category === "protocol";
  const subjectLabel = {
    protocol: "Protocol",
    medication: "Drug",
    infusion: "Infusion",
    ventilator: "Ventilator subject",
    procedure: "Procedure",
  }[category];

  const handleSubmit = async () => {
    if (needsService && !service) {
      toast.error("Select a service");
      return;
    }
    if (!subjectId || !details.trim()) {
      toast.error(`Select a ${subjectLabel.toLowerCase()} and describe the error`);
      return;
    }
    setSending(true);
    try {
      const subjectName = subjectOptions.find((s) => s.id === subjectId)?.name || subjectId;
      await axios.post(`${API}/error-reports`, {
        category,
        service: needsService ? service : "",
        subject_id: subjectId,
        subject_name: subjectName,
        details: details.trim(),
        reported_by: user?.email || "anonymous",
      });
      toast.success("Report submitted — thank you!");
      resetFields();
      setOpen(false);
    } catch {
      toast.error("Failed to submit report");
    } finally {
      setSending(false);
    }
  };

  const activeCat = CATEGORIES.find((c) => c.value === category);
  const subjectDisabled = (needsService && !service) || subjectOptions.length === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetFields(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="w-full justify-start gap-2 text-amber-400" data-testid="report-error-btn">
          <AlertTriangle className="w-4 h-4" />
          Report Error
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Report an Error
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Category chips */}
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">What's wrong?</label>
            <div className="grid grid-cols-5 gap-1.5" data-testid="report-category-chips">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => { setCategory(c.value); setSubjectId(""); }}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-1.5 py-2 transition-colors ${
                      active ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "border-border hover:bg-secondary/40 text-muted-foreground"
                    }`}
                    data-testid={`report-category-${c.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] font-medium leading-tight">{c.label}</span>
                  </button>
                );
              })}
            </div>
            {activeCat && (
              <p className="text-[10px] text-muted-foreground mt-1.5 italic pl-1">{activeCat.desc}</p>
            )}
          </div>

          {/* Service — only for protocol category */}
          {needsService && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Service</label>
              <select
                value={service}
                onChange={(e) => { setService(e.target.value); setSubjectId(""); }}
                className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                data-testid="report-service-select"
              >
                <option value="">Select service...</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Subject picker — adapts to category */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{subjectLabel}</label>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              className="w-full px-3 py-2 rounded-md border bg-background text-sm"
              disabled={subjectDisabled}
              data-testid="report-subject-select"
            >
              <option value="">
                {subjectDisabled
                  ? (needsService && !service ? "Select service first" : `No ${subjectLabel.toLowerCase()} available`)
                  : `Select ${subjectLabel.toLowerCase()}...`}
              </option>
              {subjectOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          {/* Details */}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Error Details</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={
                category === "medication" ? "e.g., 'Ketamine pediatric dose lists 2 mg/kg but guideline says 1-2 mg/kg IV'"
                  : category === "infusion" ? "e.g., 'Norepi mix recipe shows 4 mg but our bag is 8 mg in 250 mL'"
                  : category === "ventilator" ? "e.g., 'IBW calc off by 2 kg at height 178 cm'"
                  : category === "procedure" ? "e.g., 'King LT size 4 should be 70-155 cm, not 70-180'"
                  : "Describe the error or discrepancy..."
              }
              className="w-full px-3 py-2 rounded-md border bg-background text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="report-details-input"
            />
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={sending || (needsService && !service) || !subjectId || !details.trim()}
            data-testid="report-submit-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
